import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { ApiError } from "./errors";
import { pinSplit } from "./pinPlans";

/**
 * Pin orders and pins -- step 3 of the owner's pin plan (2026-09-19),
 * radaccess /access/operator/{ns}/pinorders and /pins, RBAC resources
 * `pinorder` (read, write, approve) and `pin` (read, revoke).
 *
 * 🔑 Paying does NOT credit the wallet. The buyer orders and pays; the PARENT
 * records the payment reference and approves in the console; only then are the
 * pins issued, one row and one unique key each. Rejecting issues none.
 *
 * 🔑 The plan is COPIED onto the order, so what the buyer sees on an old order
 * is what it bought, not what the plan says today.
 */
/**
 * Wholesale: awaiting_payment -> paid -> approved. Access (radaccess 5af962b):
 * awaiting_approval -> issued -- no payment, and never "approved", which is the
 * only status radbilling invoices.
 */
export type PinOrderStatus = "awaiting_payment" | "paid" | "approved" | "rejected" | "awaiting_approval" | "issued";
export type PinStatus = "available" | "used" | "locked" | "revoked";

export interface PinOrder {
  id: string;
  buyer: string;
  seller: string;
  planid: string;
  /** Absent from a radaccess older than 5af962b: read as wholesale. */
  kind?: "wholesale" | "access";
  planname: string;
  speedmbps: number;
  termdays: number;
  unitpricepaise: number;
  priceincludesgst: boolean;
  gstratebp: number;
  sac: string;
  quantity: number;
  netpaise: number;
  gstpaise: number;
  grosspaise: number;
  status: PinOrderStatus;
  paymentreference?: string;
  paymentrecordedby?: string;
  paymentrecordedat?: string;
  decidedby?: string;
  decidedat?: string;
  decisionreason?: string;
  createdby: string;
  createdat: string;
}

export interface Pin {
  id: string;
  key: string;
  orderid: string;
  seller: string;
  holder: string;
  speedmbps: number;
  termdays: number;
  status: PinStatus;
  usedaccountid?: string;
  usedref?: string;
  usedat?: string;
  closedreason?: string;
  closedby?: string;
  closedat?: string;
  replacedby?: string;
  createdat: string;
}

const orders = (ns: string) => `/operator/${encodeURIComponent(ns)}/pinorders`;
const pins = (ns: string) => `/operator/${encodeURIComponent(ns)}/pins`;

/** Orders {ns} PLACED. */
export async function listPinOrders(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<PinOrder[]> {
  const r = await callGateway<PinOrder[] | null>(identity, config().gatewayBase, orders(ns), signal ? { signal } : {});
  return r.data ?? [];
}

/** Orders placed WITH {ns} -- the parent's queue. */
export async function listIncomingPinOrders(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<PinOrder[]> {
  const r = await callGateway<PinOrder[] | null>(identity, config().gatewayBase, `${orders(ns)}/incoming`, signal ? { signal } : {});
  return r.data ?? [];
}

/**
 * Place an order. The idempotency key is the buyer's, not the server's: a
 * double-click, a retried request or a reloaded page must never buy twice.
 */
export async function createPinOrder(identity: CallerIdentity, ns: string, planId: string, quantity: number, idempotencyKey: string, seller?: string): Promise<PinOrder> {
  // seller: only for an order through a pin buyer link (owner 2026-10-10); a
  // sub-operator's seller is always its parent and is never sent.
  const r = await callGateway<PinOrder>(identity, config().gatewayBase, orders(ns), {
    method: "POST",
    body: { planid: planId, quantity, idempotencykey: idempotencyKey, ...(seller ? { seller } : {}) },
  });
  return r.data;
}

export async function recordPinOrderPayment(identity: CallerIdentity, ns: string, orderId: string, reference: string): Promise<PinOrder> {
  return decide(identity, ns, orderId, "payment", { reference });
}

export async function approvePinOrder(identity: CallerIdentity, ns: string, orderId: string): Promise<PinOrder> {
  return decide(identity, ns, orderId, "approve", undefined);
}

export async function rejectPinOrder(identity: CallerIdentity, ns: string, orderId: string, reason: string): Promise<PinOrder> {
  return decide(identity, ns, orderId, "reject", { reason });
}

async function decide(identity: CallerIdentity, ns: string, orderId: string, action: string, body?: Record<string, string>): Promise<PinOrder> {
  const r = await callGateway<PinOrder>(identity, config().gatewayBase, `${orders(ns)}/${encodeURIComponent(orderId)}/${action}`, {
    method: "PUT",
    ...(body ? { body } : {}),
  });
  return r.data;
}

/** The wallet {ns} holds. */
export async function listPins(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<Pin[]> {
  const r = await callGateway<Pin[] | null>(identity, config().gatewayBase, pins(ns), signal ? { signal } : {});
  return r.data ?? [];
}

/** Every pin {ns} issued, to every buyer -- the parent's report. */
export async function listIssuedPins(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<Pin[]> {
  const r = await callGateway<Pin[] | null>(identity, config().gatewayBase, `${pins(ns)}/issued`, signal ? { signal } : {});
  return r.data ?? [];
}

export async function revokePin(identity: CallerIdentity, ns: string, pinId: string, reason: string): Promise<Pin> {
  const r = await callGateway<Pin>(identity, config().gatewayBase, `${pins(ns)}/${encodeURIComponent(pinId)}/revoke`, {
    method: "PUT",
    body: { reason },
  });
  return r.data;
}

/**
 * The seller's invoice for an approved order -- radbilling raises and holds
 * it, radaccess proxies (step 3b, owner 2026-09-19).
 *
 * 🔑 Raising is a WRITE, and a separate grant from buying: it numbers a
 * document in the parent's statutory series. Reading never raises one, so a
 * screen may poll it safely.
 */
export interface PinOrderInvoice {
  id: string;
  invoicenumber: string;
  buyertype: string;
  buyerref: string;
  buyername: string;
  buyergstin?: string;
  suppliedlegalname?: string;
  supplierlegalname: string;
  suppliergstin?: string;
  placeofsupply: string;
  taxablevalue: number;
  totalcgst: number;
  totalsgst: number;
  totaligst: number;
  totaltax: number;
  totalamount: number;
  createdat: string;
}

export async function getPinOrderInvoice(identity: CallerIdentity, ns: string, orderId: string, signal?: AbortSignal): Promise<PinOrderInvoice | null> {
  try {
    const r = await callGateway<PinOrderInvoice>(identity, config().gatewayBase, invoicePath(ns, orderId), signal ? { signal } : {});
    return r.data ?? null;
  } catch (e) {
    // 404 is the normal answer until the invoice is raised -- not an error
    // worth showing beside an order that is otherwise fine.
    if (isNotFound(e)) return null;
    throw e;
  }
}

export async function raisePinOrderInvoice(identity: CallerIdentity, ns: string, orderId: string): Promise<PinOrderInvoice> {
  const r = await callGateway<PinOrderInvoice>(identity, config().gatewayBase, invoicePath(ns, orderId), { method: "POST" });
  return r.data;
}

const invoicePath = (ns: string, orderId: string) => `${orders(ns)}/${encodeURIComponent(orderId)}/invoice`;

/**
 * radbilling model.SaleVoucher (v0.1.112): an Internet Distributor's pin sale
 * voucher -- its own V series, gross only, NOT a tax invoice. Amount in rupees.
 */
export interface PinOrderVoucher {
  id: string;
  vouchernumber: string;
  sellerlegalname: string;
  sellergstin?: string;
  selleraddress?: string;
  buyernamespace: string;
  buyername: string;
  description: string;
  quantity: number;
  amountgross: number;
  note: string;
  issuedat: string;
  replacedbyinvoiceid?: string;
  replacedbynumber?: string;
  replacedat?: string;
}

/** Reads the voucher; null until one is issued. Never issues one. */
export async function getPinOrderVoucher(identity: CallerIdentity, ns: string, orderId: string, signal?: AbortSignal): Promise<PinOrderVoucher | null> {
  try {
    const r = await callGateway<PinOrderVoucher>(identity, config().gatewayBase, voucherPath(ns, orderId), signal ? { signal } : {});
    return r.data ?? null;
  } catch (e) {
    if (isNotFound(e)) return null;
    throw e;
  }
}

/** Issues the voucher, or returns the one already issued (radbilling is idempotent per order). */
export async function issuePinOrderVoucher(identity: CallerIdentity, ns: string, orderId: string): Promise<PinOrderVoucher> {
  const r = await callGateway<PinOrderVoucher>(identity, config().gatewayBase, voucherPath(ns, orderId), { method: "POST" });
  return r.data;
}

const voucherPath = (ns: string, orderId: string) => `${orders(ns)}/${encodeURIComponent(orderId)}/voucher`;

/**
 * A 404 from the gateway -- read from the STATUS the client recorded, never
 * from the message.
 *
 * 🪤 This used to sniff the message for "404" or "not found". radbilling's
 * answer is "no invoice has been raised for this pin order", which contains
 * neither, so the ordinary "not raised yet" reply was shown as a red error
 * (owner, 2026-09-28). The test had passed on messages invented for it; the
 * real ApiError carries `status` and `kind`, and those are what decide.
 */
export function isNotFound(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.kind === "not-found");
}

/**
 * What the order will cost, mirroring model.PinOrderTotals: the GST split is
 * taken on the ORDER TOTAL, never per pin and multiplied (CA-tagged, §13.1).
 */
export function pinOrderTotals(unitPaise: number, quantity: number, includesGST: boolean, rateBP: number): { net: number; gst: number; gross: number } {
  return pinSplit(unitPaise * quantity, includesGST, rateBP);
}

/** 1..10000, what radaccess and chk_pin_order_qty both enforce. */
export const MAX_PIN_ORDER_QUANTITY = 10000;

export function quantityError(text: string): string | null {
  if (!/^\d{1,5}$/.test(text.trim())) return "A whole number of pins";
  const n = Number(text.trim());
  if (n < 1 || n > MAX_PIN_ORDER_QUANTITY) return `1 to ${MAX_PIN_ORDER_QUANTITY} pins`;
  return null;
}

/**
 * The idempotency key for one attempt at one order. It must be STABLE while
 * the dialog is open -- a key regenerated on each render would make every
 * retry a new order -- and must not repeat across orders.
 */
export function newIdempotencyKey(): string {
  const b = new Uint8Array(16);
  globalThis.crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

export const PIN_ORDER_STATUS_LABEL: Record<PinOrderStatus, string> = {
  awaiting_payment: "Awaiting payment",
  paid: "Paid, awaiting approval",
  approved: "Approved",
  rejected: "Rejected",
  awaiting_approval: "Awaiting approval",
  issued: "Issued",
};

export const PIN_STATUS_LABEL: Record<PinStatus, string> = {
  available: "Available",
  used: "Used",
  locked: "Locked",
  revoked: "Revoked",
};

type Severity = "default" | "info" | "success" | "warning" | "error";

const ORDER_COLOUR: Record<PinOrderStatus, Severity> = {
  awaiting_payment: "warning",
  paid: "info",
  approved: "success",
  rejected: "error",
  awaiting_approval: "info",
  issued: "success",
};

const PIN_COLOUR: Record<PinStatus, Severity> = {
  available: "success",
  used: "default",
  locked: "warning",
  revoked: "error",
};

export function pinOrderStatusColour(s: PinOrderStatus): Severity {
  return ORDER_COLOUR[s] ?? "default";
}

export function pinStatusColour(s: PinStatus): Severity {
  return PIN_COLOUR[s] ?? "default";
}

/** Which of the parent's actions this order is actually open to. */
export function pinOrderActions(o: PinOrder): { payment: boolean; approve: boolean; reject: boolean } {
  return {
    payment: o.status === "awaiting_payment",
    // 🔑 The owner's rule: approval comes AFTER the payment is recorded, never
    // straight off an unpaid order. radaccess refuses it either way.
    // An access order has no payment step: it is approved straight from
    // awaiting_approval.
    approve: o.status === "paid" || o.status === "awaiting_approval",
    reject: o.status === "awaiting_payment" || o.status === "paid" || o.status === "awaiting_approval",
  };
}

/**
 * An invoice exists for a SUPPLY, so only an approved order has one. Asking
 * about any other order is asking about a sale that has not happened.
 */
export function canInvoicePinOrder(o: PinOrder): boolean {
  // An access pin is never invoiced (§15): its order ends "issued", not
  // "approved", and the kind is checked too in case a later status is added.
  return o.status === "approved" && o.kind !== "access";
}

/** Whether an order carries money at all. */
export function isAccessOrder(o: PinOrder): boolean {
  return o.kind === "access";
}

/** Only an unused pin can be revoked -- a used one is a supply already made. */
export function canRevokePin(p: Pin): boolean {
  return p.status === "available";
}

export function pinWalletCount(list: Pin[]): { available: number; used: number; closed: number } {
  return {
    available: list.filter((p) => p.status === "available").length,
    used: list.filter((p) => p.status === "used").length,
    closed: list.filter((p) => p.status === "locked" || p.status === "revoked").length,
  };
}
